/**
 * RestoID — link a phone number as a 2FA factor.
 * ---------------------------------------------------------------------------
 * RestoID frame around the PRISTINE upstream `VerificationCodeMfaBinding`
 * logic, instantiated the way `src/pages/MfaBinding/PhoneMfaBinding` does it
 * (identifierType = Phone, `invalid_phone` as the guard error):
 *   - flow state ......... `useMfaFlowState` + the same invalid-session guard
 *   - send the code ...... `sendVerificationCode(InteractionEvent.Register, …)`
 *   - hand-off ........... `setVerificationId` / `setIdentifierInputValue` then
 *                          `/continue/verification-code` with the SAME state
 *                          shape (`{ flow: MfaBinding, mfaFlowState }`)
 *   - error branch ....... `useErrorHandler` with the `guard.invalid_input`
 *                          handler; everything else still falls through to the
 *                          upstream toast
 *   - skip ............... `useSkipMfa` / `useSkipOptionalMfa`, chosen by
 *                          `skippable` + `suggestion` exactly as upstream
 *   - switch factor ...... shared `MfaSwitchLink` (same >1-factor condition)
 * Swapped in for `./pages/MfaBinding/PhoneMfaBinding` via `src/App.tsx`.
 *
 * Deliberate differences from upstream:
 * 1. SecondaryPageLayout (NavBar + its own duplicated title/description) is
 *    replaced by FirstScreenLayout + a single centred `ScreenHead`. The NavBar's
 *    back action becomes ScreenHead's back pill, and its "skip" becomes the
 *    bottom `GhostAction` — the same place `custom/pages/MfaBinding` puts it.
 *    Upstream's description (`mfa.link_phone_2fa_description`, "Link your phone
 *    number for 2-step verification") only restates the title ("Link your phone
 *    number"), so it is dropped rather than repeated under it as a `sub` — the
 *    same call MfaTotpBinding and MfaBackupCodeBinding make.
 * 2. `IdentifierProfileForm` + `SmartInputField` become the RestoID
 *    `PhoneInput` (fixed +7, RU mask), as on every other RestoID phone screen.
 *    The form is still react-hook-form with upstream's `reValidateMode: 'onBlur'`
 *    and upstream's clear-on-invalid effect; the field holds the MASKED string
 *    and `phoneToIdentifierValue` turns it into the very value upstream
 *    submitted (`${countryCode}${nationalNumber}`, no "+"), so
 *    `validateIdentifierField` and the API see identical input.
 * 3. Upstream passed `experienceSettings?.usernamePolicy` to
 *    `validateIdentifierField`; it is only ever read for a Username identifier,
 *    and this screen is Phone-only, so the PageContext read is dropped.
 * 4. Upstream fed the raw `invalid_phone` KEY into `<ErrorMessage>` as children,
 *    which renders untranslated — it shows the literal string "invalid_phone".
 *    We translate it (`t('error.invalid_phone')`), which is plainly what was
 *    meant; the branch itself is untouched.
 * 5. Unlike the sign-in / forgot-password phone screens, the CTA is NOT disabled
 *    until the number is complete. Upstream lets an empty or partial submit
 *    through so the "required" / "invalid phone" messages can fire, and losing
 *    that error branch would be worse than the extra dead click.
 * 6. `PhoneInput` does not YET expose `autoFocus`, `inputRef` or `onBlur` — its
 *    props are still `{ value, onChange, danger }` — so upstream's autofocus and
 *    RHF's focus-the-invalid-field are dropped here, matching `ForgotPassword`
 *    and `SignIn`, which likewise do not pop the keyboard on a phone field. The
 *    real fix is to add those props to `PhoneInput` in
 *    `custom/components/restoid.tsx` (the sibling `Field` already carries the
 *    equivalent form-binding block); until that lands, the blur that
 *    `reValidateMode: 'onBlur'` needs is caught on the wrapper div below.
 */
import {
  InteractionEvent,
  SignInIdentifier,
  type VerificationCodeIdentifier,
} from '@logto/schemas';
import { type FormEvent, useCallback, useContext, useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import FirstScreenLayout from '@/Layout/FirstScreenLayout';
import UserInteractionContext from '@/Providers/UserInteractionContextProvider/UserInteractionContext';
import { sendVerificationCode } from '@/apis/experience';
import useErrorHandler from '@/hooks/use-error-handler';
import useMfaFlowState from '@/hooks/use-mfa-factors-state';
import useNavigateWithPreservedSearchParams from '@/hooks/use-navigate-with-preserved-search-params';
import useSkipMfa from '@/hooks/use-skip-mfa';
import useSkipOptionalMfa from '@/hooks/use-skip-optional-mfa';
import { UserMfaFlow } from '@/types';
import { getGeneralIdentifierErrorMessage, validateIdentifierField } from '@/utils/form';
import { codeVerificationTypeMap } from '@/utils/sign-in-experience';

import { MfaSwitchLink } from '../components/MfaFactorRows';
import {
  Button,
  C,
  FONT,
  GhostAction,
  PhoneInput,
  ScreenHead,
  Stack,
  phoneNsn,
  phoneToIdentifierValue,
} from '../components/restoid';
import { useCopy } from '../copy';

import ErrorPage from './ErrorPage';

type FormState = { phone: string };

const MfaPhoneBinding = () => {
  const { t } = useTranslation();
  const c = useCopy();
  const navigate = useNavigateWithPreservedSearchParams();
  const mfaFlowState = useMfaFlowState();
  const { setVerificationId, setIdentifierInputValue } = useContext(UserInteractionContext);
  const [errorMessage, setErrorMessage] = useState<string>();

  const skipMfa = useSkipMfa();
  const skipOptionalMfa = useSkipOptionalMfa();
  const handleError = useErrorHandler();

  const {
    control,
    handleSubmit,
    formState: { errors, isValid, isSubmitting },
  } = useForm<FormState>({
    reValidateMode: 'onBlur',
    defaultValues: { phone: '' },
  });

  const clearErrorMessage = useCallback(() => {
    setErrorMessage('');
  }, []);

  // Upstream IdentifierProfileForm: the lookup error is dropped as soon as the
  // form stops being valid, so a stale "invalid phone" never outlives the input.
  useEffect(() => {
    if (!isValid) {
      clearErrorMessage();
    }
  }, [clearErrorMessage, isValid]);

  const onSubmitHandler = useCallback(
    async (event?: FormEvent<HTMLFormElement>) => {
      clearErrorMessage();

      await handleSubmit(async ({ phone }) => {
        const identifier: VerificationCodeIdentifier = {
          type: SignInIdentifier.Phone,
          value: phoneToIdentifierValue(phone),
        };

        try {
          const result = await sendVerificationCode(InteractionEvent.Register, identifier);

          setVerificationId(codeVerificationTypeMap[SignInIdentifier.Phone], result.verificationId);
          setIdentifierInputValue(identifier);

          navigate('/continue/verification-code', {
            state: {
              flow: UserMfaFlow.MfaBinding,
              mfaFlowState,
            },
          });
        } catch (error) {
          await handleError(error, {
            'guard.invalid_input': () => {
              setErrorMessage(t('error.invalid_phone'));
            },
          });
        }
      })(event);
    },
    [
      clearErrorMessage,
      handleError,
      handleSubmit,
      mfaFlowState,
      navigate,
      setIdentifierInputValue,
      setVerificationId,
      t,
    ]
  );

  if (!mfaFlowState) {
    return <ErrorPage title="error.invalid_session" />;
  }

  const { skippable, suggestion } = mfaFlowState;
  const onSkip = skippable ? (suggestion ? skipOptionalMfa : skipMfa) : undefined;
  // One error slot: the field's own validation message wins over the API error.
  const shownError = errors.phone?.message ?? errorMessage;

  return (
    <FirstScreenLayout pageMeta={{ titleKey: 'mfa.link_phone_verification_code_description' }}>
      <Stack gap={20}>
        <ScreenHead
          back={() => navigate(-1)}
          backLabel={c('backShort')}
          title={t('mfa.link_phone_verification_code_description')}
        />
        <form onSubmit={onSubmitHandler}>
          <Stack gap={18}>
            <Controller
              control={control}
              name="phone"
              rules={{
                validate: (value) => {
                  if (!phoneNsn(value)) {
                    return getGeneralIdentifierErrorMessage([SignInIdentifier.Phone], 'required');
                  }

                  const invalid = validateIdentifierField(
                    SignInIdentifier.Phone,
                    phoneToIdentifierValue(value)
                  );

                  if (!invalid) {
                    return true;
                  }

                  return typeof invalid === 'string'
                    ? t(`error.${invalid}`)
                    : t(`error.${invalid.code}`, invalid.data ?? {});
                },
              }}
              render={({ field: { value, onChange, onBlur } }) => (
                // `reValidateMode: 'onBlur'` only re-runs the rules if RHF's own
                // onBlur fires. PhoneInput exposes no onBlur prop, so we catch the
                // inner input's blur as it bubbles through this wrapper — same
                // moment upstream's SmartInputField reported it.
                <div onBlur={onBlur}>
                  <label
                    style={{
                      display: 'block',
                      color: C.sub,
                      font: `500 13px/1 ${FONT}`,
                      marginBottom: 9,
                    }}
                  >
                    {c('phoneLabel')}
                  </label>
                  {/* PhoneInput owns its own mask; it has no label / error slot. */}
                  <PhoneInput value={value} danger={Boolean(shownError)} onChange={onChange} />
                  {shownError ? (
                    <div
                      role="alert"
                      style={{ marginTop: 8, color: C.danger, font: `400 12.5px/1.45 ${FONT}` }}
                    >
                      {shownError}
                    </div>
                  ) : null}
                </div>
              )}
            />
            <Button title={t('action.continue')} htmlType="submit" isLoading={isSubmitting} />
          </Stack>
        </form>
        <MfaSwitchLink flow={UserMfaFlow.MfaBinding} flowState={mfaFlowState} />
        {onSkip ? <GhostAction onClick={() => onSkip()}>{t('action.nav_skip')}</GhostAction> : null}
      </Stack>
    </FirstScreenLayout>
  );
};

export default MfaPhoneBinding;
